import { useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { sendInvoiceEmail } from '../api/endpoints/invoices';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import { ErrorBanner } from './ErrorBanner';

/**
 * Mandar una factura al correo del cliente: the Facturas row and the Cobrado
 * card send the same way, with the same words.
 */
export function useInvoiceMail(invoiceId: string, onSent?: () => void) {
  const [email, setEmail] = useState('');
  // A fresh key per press: a replay after a dropped response answers with the
  // send that already happened, and a deliberate retry genuinely retries.
  const sendKey = useRef(createIdempotencyKeyHolder()).current;
  const mutation = useMutation({
    mutationFn: () => sendInvoiceEmail(invoiceId, email.trim(), sendKey.ensure()),
    onSuccess: () => {
      setEmail('');
      onSent?.();
    },
    onSettled: () => sendKey.reset(),
  });
  return { email, setEmail, mutation };
}

type InvoiceMail = ReturnType<typeof useInvoiceMail>;

/** What happened to the last send: an error, the address it went to, or a plain "did not leave". */
export function InvoiceMailFeedback({ mail }: { mail: InvoiceMail }) {
  const { mutation } = mail;
  return (
    <>
      {mutation.isError ? <ErrorBanner message={errorMessage(mutation.error)} /> : null}
      {mutation.data?.status === 'sent' ? (
        <p className="field-help">Factura enviada a {mutation.data.email}.</p>
      ) : null}
      {mutation.data?.status === 'failed' ? (
        <p className="field-help">No salió el correo. Prueba de nuevo.</p>
      ) : null}
    </>
  );
}

type InvoiceMailFormProps = { invoiceId: string; mail: InvoiceMail; onCancel: () => void };

export function InvoiceMailForm({ invoiceId, mail, onCancel }: InvoiceMailFormProps) {
  const { email, setEmail, mutation } = mail;
  return (
    <form
      className="filter-row"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (email.trim() === '') return;
        mutation.mutate();
      }}
    >
      <div className="field">
        <label className="field-label" htmlFor={`send-email-${invoiceId}`}>
          Correo del cliente
        </label>
        <input
          id={`send-email-${invoiceId}`}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="cliente@correo.com"
          required
        />
      </div>
      <button type="submit" className="btn small" disabled={mutation.isPending}>
        {mutation.isPending ? 'Enviando…' : 'Enviar'}
      </button>
      <button type="button" className="btn small ghost" onClick={onCancel} disabled={mutation.isPending}>
        Cancelar
      </button>
    </form>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}

/** The Cobrado card's "Enviar por correo": a quiet button that opens the same form the Facturas row uses. */
export function InvoiceMailer({ invoiceId }: { invoiceId: string }) {
  const [open, setOpen] = useState(false);
  const mail = useInvoiceMail(invoiceId, () => setOpen(false));
  return (
    <div>
      <InvoiceMailFeedback mail={mail} />
      {open ? (
        <InvoiceMailForm invoiceId={invoiceId} mail={mail} onCancel={() => setOpen(false)} />
      ) : (
        <button type="button" className="btn small ghost" onClick={() => setOpen(true)}>
          Enviar por correo
        </button>
      )}
    </div>
  );
}
